import { NgClass, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  Signal,
  signal,
  WritableSignal,
} from '@angular/core';
import { ApiHttpResponse, IconType, VideoSection } from '@kinomap-dev/ng-ui-lib/core';
import { EmptyContentListElement } from '../../../components/empty-content-list-element/empty-content-list-element';
import { Paginator } from '../../../components/paginator/paginator';
import { Table } from '../../../components/table/table';
import { ProfileService } from '../../../services/profile.service';
import { TableContentService, CONTENT_TYPE_TOKEN } from '../../../services/table-content.service';
import { convertVideoSectionToScenicContentTableItem } from '../../../utils/video-section-to-scenic-content-table-data.helpers';
import { IconComponent } from '@kinomap-dev/ng-ui-lib/basics';
import { ColorService } from '../../../services/color.service';
import { FilterService } from '../../../services/filters.service';
import { TableColumnsService } from '../../../services/table-columns.service';

import { TranslateModule } from '@ngx-translate/core';
const tableHeaderElements: string[] = [
  'country',
  'sport',
  'duration',
  'distance',
  'slope',
  'level',
  'status',
  'sessions',
  'comments',
  'likes',
  'upload',
  // 'labels', // Hidden while the tags topic is on standby
];

@Component({
  selector: 'app-scenic-list',
  imports: [TranslateModule, 
    NgTemplateOutlet,
    EmptyContentListElement,
    Paginator,
    Table,
    // NgClass, IconComponent: used by the selection header, disabled for now
  ],
  templateUrl: './scenic-list.html',
  styleUrl: '../content-list.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    FilterService,
    TableContentService,
    TableColumnsService,
    { provide: CONTENT_TYPE_TOKEN, useValue: 'scenic' },
  ],
})
export class ScenicList {
  readonly colorService: ColorService = inject(ColorService);
  readonly actions: Signal<IconType[]> = signal<IconType[]>(['edit', 'options']);
  public readonly profileService: ProfileService = inject(ProfileService);
  public scenicTableContentService: TableContentService = inject(TableContentService);
  readonly tableColumnsService: TableColumnsService = inject(TableColumnsService);
  readonly emptyList: Signal<boolean> = computed(
    () => (this.profileService.profile()?.videos_count ?? 0) === 0,
  );

  readonly scenicTableData: WritableSignal<ApiHttpResponse<VideoSection[]> | undefined> = this
    .scenicTableContentService.tableData as WritableSignal<
      ApiHttpResponse<VideoSection[]> | undefined
    >;

  private profileScenicCount = computed(() => this.profileService.profile()?.videos_count ?? 0);
  readonly skeletonCount: Signal<number> = computed(() => {
    if (this.profileScenicCount() < this.scenicTableContentService.contentLimitPerPage()) {
      return this.profileScenicCount();
    }
    return this.scenicTableContentService.contentLimitPerPage();
  });

  readonly checkboxVisible: WritableSignal<boolean> = signal<boolean>(false);
  readonly tableColumns: Signal<string[]> = computed(() =>
    this.checkboxVisible()
      ? ['checkbox', ...this.tableColumnsService.visibleColumns()]
      : this.tableColumnsService.visibleColumns(),
  );
  readonly selectedActions = signal<IconType[]>(['add_playlist', 'eye_on']);

  readonly tableData: Signal<any[] | undefined> = computed(() =>
    this.selectedAll()
      ? this.tableDataFormated?.map((item: any) => ({ ...item, checkbox: true }))
      : this.tableDataFormated,
  );

  readonly selectedAll: WritableSignal<boolean> = signal(false);

  selectedItems: WritableSignal<number[]> = signal([]);
  readonly selectedItemCount: Signal<number> = computed(() => this.selectedItems().length);
  readonly selectedItemCompleted: Signal<boolean> = computed<boolean>(
    () => this.selectedItemCount() === this.tableDataFormated?.length,
  );
  readonly selectedItemPartial: Signal<boolean> = computed<boolean>(
    () => this.selectedItemCount() > 0 && !this.selectedItemCompleted(),
  );

  private get tableDataFormated(): any[] | undefined {
    const data: VideoSection[] | undefined = this.scenicTableData()?.data;
    if (!data || !Array.isArray(data)) return undefined;
    return data.map((row: VideoSection) => {
      return convertVideoSectionToScenicContentTableItem(row);
    });
  }

  constructor() {
    this.tableColumnsService.setColumns(tableHeaderElements);
  }

  manageSeletedItemList(id: number): void {
    const isIncluded: boolean = this.selectedItems().includes(id);
    if (isIncluded) {
      this.selectedItems.set(this.selectedItems().filter((_id: number) => _id !== id));
    } else {
      this.selectedItems.set([...this.selectedItems(), id]);
    }

    if (this.selectedItemCompleted()) {
      this.selectedAll.set(true);
    } else if (this.selectedItemCount() === 0) {
      this.selectedAll.set(false);
    }
  }

  toggleSelectAll(): void {
    const newValue: boolean = !this.selectedAll();
    this.selectedAll.set(newValue);
    if (newValue) {
      this.selectedItems.set(this.tableDataFormated?.map((item: any) => item.id) ?? []);
    } else {
      this.selectedItems.set([]);
    }
  }
}
